import React, { useEffect, useMemo, useState } from "react";
import SearchBar from "@/components/SearchBar";
import Segmented from "@/components/Segmented";
import { api } from "../lib/api";
import { invalidateAppSettings } from "../lib/useAppSettings";
import { GcConfirmSection, SchedulingSection } from "../components/SchedulingSettingsSections";
import { GcRulesSection } from "../components/GcRulesSettings";
import { ScheduleAutomationSection, VehiclesSection } from "../components/ScheduleAutomationSettings";
import QuestionSetsPanel from "../components/QuestionSetsPanel";
import TakeoffSettings from "../../drawings/TakeoffSettings";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Users, User, Trash2, Plus, Save, RefreshCw, RotateCcw, Link2 } from "lucide-react";
import { useRole } from "../context/RoleContext";
import { linkTarget, setLinkTarget } from "../../drawings/openDrawing";

const ROLE_OPTIONS = ["Installer", "Lead", "Foreman", "PM", "Warehouse", "Sub", "Manager"];
const DEFAULT_REGIONS = ["Denver", "COS", "Service", "Out-of-town"];

// Format any phone-ish input into "(303) 313-3333".
// Returns the original string if we can't extract a 10-digit US number.
function formatPhone(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === "1") {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
}

// Reusable in-app confirmation toast. Sonner-based, non-modal — replaces
// window.confirm() which can be silently blocked by some browsers / iframes.
function confirmToast(message, onConfirm, { confirmLabel = "Delete", danger = true } = {}) {
  toast(message, {
    duration: 8000,
    action: {
      label: confirmLabel,
      onClick: () => onConfirm(),
    },
    cancel: { label: "Cancel" },
    classNames: danger ? { actionButton: "!bg-red-600 !text-white hover:!bg-red-700" } : undefined,
  });
}

// ---------------- Installers sub-tab ----------------
function InstallersSection() {
  const [installers, setInstallers] = useState([]);
  const [crews, setCrews] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCrew, setFilterCrew] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [newBadge, setNewBadge] = useState("");
  // Inline-editor state for the per-row Badges popover
  const [badgePopoverId, setBadgePopoverId] = useState(null);
  const [badgeAnchorRect, setBadgeAnchorRect] = useState({ top: 0, left: 0 });
  const [badgeFacilityDraft, setBadgeFacilityDraft] = useState("");
  // Duplicate-finder utility
  const [dupesOpen, setDupesOpen] = useState(false);
  const [dupeGroups, setDupeGroups] = useState([]);
  const [dupesLoading, setDupesLoading] = useState(false);

  const load = async () => {
    const [a, b, c, d] = await Promise.all([
      api.get("/installers"), api.get("/crews"), api.get("/scope-codes"), api.get("/projects"),
    ]);
    setInstallers(a.data || []);
    setCrews(b.data || []);
    setScopes(c.data || []);
    setProjects(d.data || []);
  };
  useEffect(() => { load(); }, []);

  // Derive region suggestions (defaults + whatever is already in use)
  const regionOptions = useMemo(() => {
    const s = new Set(DEFAULT_REGIONS);
    installers.forEach((i) => { if (i.home_region) s.add(i.home_region); });
    return [...s];
  }, [installers]);

  const save = async () => {
    try {
      // Sanitize empty skill_scores (remove 0s so the map stays small)
      const ss = Object.fromEntries(
        Object.entries(editing.skill_scores || {}).filter(([, v]) => v && v > 0),
      );
      const payload = { ...editing, skill_scores: ss, phone: formatPhone(editing.phone || "") };
      if (editing.id) {
        await api.patch(`/installers/${editing.id}`, payload);
        toast.success("Installer updated");
      } else {
        await api.post("/installers", payload);
        toast.success("Installer added");
      }
      setEditing(null);
      load();
    } catch { toast.error("Save failed"); }
  };

  const archive = async (id, name) => {
    confirmToast(`Archive ${name || "this installer"}?`, async () => {
      try {
        await api.delete(`/installers/${id}`);
        toast.success("Archived");
        load();
      } catch { toast.error("Archive failed"); }
    }, { confirmLabel: "Archive", danger: false });
  };

  const restore = async (id) => {
    try {
      await api.patch(`/installers/${id}`, { active: true });
      toast.success("Restored");
      load();
    } catch { toast.error("Restore failed"); }
  };

  const purge = async (id, name) => {
    confirmToast(
      `Permanently delete ${name || "this installer"}? This cannot be undone.`,
      async () => {
        try {
          await api.delete(`/installers/${id}/purge`);
          toast.success("Permanently deleted");
          load();
        } catch (e) {
          const msg = e?.response?.data?.detail || "Permanent delete failed";
          toast.error(msg);
        }
      },
      { confirmLabel: "Permanently delete" },
    );
  };

  const toggleActive = async (inst) => {
    try {
      await api.patch(`/installers/${inst.id}`, { active: !inst.active });
      load();
    } catch { toast.error("Could not update"); }
  };

  // Optimistic inline patch — updates local state then persists.
  const patchInline = async (id, payload) => {
    setInstallers((arr) => arr.map((x) => (x.id === id ? { ...x, ...payload } : x)));
    try {
      await api.patch(`/installers/${id}`, payload);
    } catch {
      toast.error("Save failed");
      load();
    }
  };

  const addBadgeInline = (inst, value) => {
    if (!value) return;
    const next = [...new Set([...(inst.badges || []), value])];
    patchInline(inst.id, { badges: next });
  };
  const removeBadgeInline = (inst, value) => {
    const next = (inst.badges || []).filter((b) => b !== value);
    patchInline(inst.id, { badges: next });
  };

  // ── Duplicate finder & merge ─────────────────────────────────────────────
  const openDupesDialog = async () => {
    setDupesOpen(true);
    setDupesLoading(true);
    try {
      const r = await api.get("/admin/duplicate-installers");
      setDupeGroups(r.data?.groups || []);
    } catch {
      toast.error("Could not load duplicates");
    } finally {
      setDupesLoading(false);
    }
  };
  const mergeDupes = async (keepId, dropId) => {
    try {
      const r = await api.post("/admin/merge-installers", { keep_id: keepId, drop_id: dropId });
      const fields = r.data?.fields_copied || [];
      toast.success(
        `Merged · ${r.data.assignments_reassigned} assignment${r.data.assignments_reassigned === 1 ? "" : "s"} reassigned${fields.length ? ` · copied ${fields.join(", ")}` : ""}`,
      );
      // Refresh both the duplicate list and the main installer list
      const [dupR] = await Promise.all([api.get("/admin/duplicate-installers"), load()]);
      setDupeGroups(dupR.data?.groups || []);
    } catch {
      toast.error("Merge failed");
    }
  };

  const filtered = installers.filter((i) => {
    if ((i.active !== false) === showArchived) return false;
    if (filterCrew && i.crew_id !== filterCrew) return false;
    if (search) {
      const s = search.toLowerCase();
      return [i.name, i.email, i.phone, i.role, i.home_region].some((f) => (f || "").toLowerCase().includes(s));
    }
    return true;
  });
  const crewById = Object.fromEntries(crews.map((c) => [c.id, c]));
  const projectById = Object.fromEntries(projects.map((p) => [p.id, p]));

  const badgeLabel = (b) => {
    if (b.startsWith("proj:")) {
      const p = projectById[b.slice(5)];
      return p ? `${p.project_name}${p.job_number ? ` (#${p.job_number})` : ""}` : "Job";
    }
    if (b.startsWith("facility:")) return b.slice(9);
    return b;
  };

  // Cycle scope score: undefined/0 -> 50 -> 100 -> 0
  const cycleSkill = (code) => {
    const cur = (editing.skill_scores || {})[code] || 0;
    const nxt = cur === 0 ? 50 : cur === 50 ? 100 : 0;
    setEditing({ ...editing, skill_scores: { ...(editing.skill_scores || {}), [code]: nxt } });
  };

  const addBadge = (value) => {
    if (!value) return;
    const existing = new Set(editing.badges || []);
    existing.add(value);
    setEditing({ ...editing, badges: [...existing] });
    setNewBadge("");
  };
  const removeBadge = (b) => {
    setEditing({ ...editing, badges: (editing.badges || []).filter((x) => x !== b) });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <SearchBar value={search} onChange={setSearch}
                   placeholder="Search..." testId="installer-search" />
        <div className="w-px h-6 bg-slate-200" />
        <Segmented
          options={[{ key: "active", label: "Active", testId: "installer-show-active" },
                    { key: "archived", label: "Archived", testId: "installer-show-archived" }]}
          value={showArchived ? "archived" : "active"}
          onChange={(k) => setShowArchived(k === "archived")}
        />
        <select
          className="h-8 text-sm border border-gray-300 rounded-md px-2 bg-white"
          value={filterCrew}
          onChange={(e) => setFilterCrew(e.target.value)}
          data-testid="installer-filter-crew"
        >
          <option value="">All crews</option>
          {crews.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <span className="ml-2 text-xs text-gray-500">{filtered.length} of {installers.length} shown</span>
        <Button
          size="sm"
          variant="outline"
          className="ml-auto"
          data-testid="installer-find-duplicates-btn"
          onClick={openDupesDialog}
        >
          Find Duplicates
        </Button>
        <Button
          size="sm"
          className=""
          data-testid="installer-add-btn"
          onClick={() => setEditing({
            name: "", employee_id: "", role: "Installer", home_region: "Denver", crew_id: "",
            standing_assignment: "", skills: [], skill_scores: {}, badges: [],
            drug_test_cleared: false, active: true, email: "", phone: "",
            address: "", max_hours_per_day: 10, vehicle: "", hire_date: "",
            emergency_contact: "", notes: "",
          })}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add installer
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-xs text-gray-500 uppercase tracking-wider">
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium">ID</th>
              <th className="text-left px-3 py-2 font-medium">Crew</th>
              <th className="text-left px-3 py-2 font-medium">Region</th>
              <th className="text-left px-3 py-2 font-medium">Email</th>
              <th className="text-left px-3 py-2 font-medium">Phone</th>
              <th className="text-left px-3 py-2 font-medium">Role</th>
              <th className="text-left px-3 py-2 font-medium">Standing</th>
              <th className="text-left px-3 py-2 font-medium">Badges</th>
              <th className="text-left px-3 py-2 font-medium">Drug Free</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id} className="border-t border-gray-100 hover:bg-gray-50 align-middle">
                <td className="px-3 py-2 font-medium text-gray-900 whitespace-nowrap">{i.name}</td>
                <td className="px-3 py-2 text-xs text-gray-500 font-mono">{i.employee_id || "—"}</td>
                <td className="px-3 py-2">
                  {i.crew_id ? (
                    <span className="inline-flex items-center gap-1.5 text-xs whitespace-nowrap">
                      <span className="w-3 h-3 rounded-sm border border-gray-300" style={{ background: crewById[i.crew_id]?.color || "#999" }}></span>
                      {crewById[i.crew_id]?.name || "—"}
                    </span>
                  ) : <span className="text-xs text-gray-400">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{i.home_region || "—"}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">
                  {i.email ? (
                    <a
                      href={`mailto:${i.email}`}
                      className="text-blue-600 hover:underline"
                      data-testid={`installer-email-${i.id}`}
                    >{i.email}</a>
                  ) : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">
                  {i.phone ? (
                    <a
                      href={`tel:${String(i.phone).replace(/\D/g, "")}`}
                      className="text-blue-600 hover:underline"
                      data-testid={`installer-phone-${i.id}`}
                    >{formatPhone(i.phone)}</a>
                  ) : <span className="text-gray-400">—</span>}
                </td>
                {/* ── Role (inline editable) ── */}
                <td className="px-2 py-1.5">
                  <select
                    data-testid={`installer-role-${i.id}`}
                    value={i.role || "Installer"}
                    onChange={(e) => patchInline(i.id, { role: e.target.value, is_lead: ["Foreman", "Lead"].includes(e.target.value) })}
                    className="h-7 text-xs border border-gray-200 rounded px-1.5 bg-white hover:border-gray-300 focus:border-blue-400 focus:outline-none"
                  >
                    {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                {/* ── Standing (inline editable) ── */}
                <td className="px-2 py-1.5">
                  <Input
                    data-testid={`installer-standing-${i.id}`}
                    className="h-7 text-xs w-32"
                    defaultValue={i.standing_assignment || ""}
                    onBlur={(e) => {
                      const v = e.target.value;
                      if (v !== (i.standing_assignment || "")) patchInline(i.id, { standing_assignment: v });
                    }}
                    placeholder="—"
                  />
                </td>
                {/* ── Badges (inline popover editor) ── */}
                <td className="px-2 py-1.5 relative">
                  <button
                    type="button"
                    data-testid={`installer-badges-${i.id}`}
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect();
                      setBadgeAnchorRect({ top: r.bottom + 4, left: Math.min(r.left, window.innerWidth - 300) });
                      setBadgePopoverId(badgePopoverId === i.id ? null : i.id);
                      setBadgeFacilityDraft("");
                    }}
                    className="inline-flex items-center gap-1 text-xs px-1.5 py-1 rounded hover:bg-gray-100 border border-transparent hover:border-gray-200"
                    title={(i.badges || []).map(badgeLabel).join(", ") || "Add badges"}
                  >
                    {(i.badges && i.badges.length) ? (
                      <span className="inline-block bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5 font-medium">
                        {i.badges.length}
                      </span>
                    ) : <span className="text-gray-400">+ add</span>}
                    <span className="text-gray-400 text-[10px]">▾</span>
                  </button>
                  {badgePopoverId === i.id && (
                    <>
                      <div className="fixed inset-0 z-30" onClick={() => setBadgePopoverId(null)} />
                      {/* fixed-position so the card's overflow-x-auto doesn't clip it */}
                      <div className="fixed z-40 w-72 bg-white border border-gray-200 rounded-md shadow-lg p-2 space-y-2"
                        style={{ top: badgeAnchorRect.top, left: badgeAnchorRect.left }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`installer-badges-popover-${i.id}`}>
                        {(i.badges || []).length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {(i.badges || []).map((b) => (
                              <span key={b} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                                {badgeLabel(b)}
                                <button type="button" onClick={() => removeBadgeInline(i, b)} className="hover:text-red-600">×</button>
                              </span>
                            ))}
                          </div>
                        )}
                        <select
                          className="h-7 w-full text-xs border border-gray-300 rounded-md px-1.5 bg-white"
                          value=""
                          onChange={(e) => { if (e.target.value) addBadgeInline(i, `proj:${e.target.value}`); }}
                        >
                          <option value="">+ Add job badge…</option>
                          {projects.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.project_name}{p.job_number ? ` (#${p.job_number})` : ""}
                            </option>
                          ))}
                        </select>
                        <div className="flex items-center gap-1">
                          <Input
                            placeholder="or facility (e.g. DIA)"
                            value={badgeFacilityDraft}
                            onChange={(e) => setBadgeFacilityDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && badgeFacilityDraft.trim()) {
                                e.preventDefault();
                                addBadgeInline(i, `facility:${badgeFacilityDraft.trim()}`);
                                setBadgeFacilityDraft("");
                              }
                            }}
                            className="h-7 text-xs"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7"
                            onClick={() => {
                              if (badgeFacilityDraft.trim()) {
                                addBadgeInline(i, `facility:${badgeFacilityDraft.trim()}`);
                                setBadgeFacilityDraft("");
                              }
                            }}
                          >Add</Button>
                        </div>
                      </div>
                    </>
                  )}
                </td>
                {/* ── Drug Free (inline toggle) ── */}
                <td className="px-2 py-1.5">
                  <input
                    type="checkbox"
                    data-testid={`installer-drug-${i.id}`}
                    checked={!!i.drug_test_cleared}
                    onChange={(e) => patchInline(i.id, { drug_test_cleared: e.target.checked })}
                    className="w-4 h-4 cursor-pointer"
                  />
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setEditing({ ...i, skill_scores: i.skill_scores || {}, badges: i.badges || [] })} data-testid={`installer-edit-${i.id}`}>Edit</Button>
                  {i.active === false ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => restore(i.id)} data-testid={`installer-restore-${i.id}`} title="Restore from archive">
                        <RotateCcw className="w-3.5 h-3.5 text-emerald-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => purge(i.id, i.name)} data-testid={`installer-purge-${i.id}`} title="Permanently delete">
                        <Trash2 className="w-3.5 h-3.5 text-red-600" />
                      </Button>
                    </>
                  ) : (
                    <Button variant="ghost" size="sm" onClick={() => archive(i.id, i.name)} data-testid={`installer-delete-${i.id}`} title="Archive">
                      <Trash2 className="w-3.5 h-3.5 text-gray-500" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={11} className="px-3 py-10 text-center text-gray-500">{showArchived ? "No archived installers." : "No installers found."}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <header className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-sm font-semibold">{editing.id ? "Edit installer" : "Add installer"}</h3>
              <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>Close</Button>
            </header>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div><label className="text-xs text-gray-600">Name *</label>
                <Input data-testid="inst-edit-name" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><label className="text-xs text-gray-600">Employee ID</label>
                <Input data-testid="inst-edit-employee-id" value={editing.employee_id || ""} onChange={(e) => setEditing({ ...editing, employee_id: e.target.value })} placeholder="e.g., 1284" /></div>
              <div><label className="text-xs text-gray-600">Role</label>
                <select data-testid="inst-edit-role" className="h-9 w-full text-sm border border-gray-300 rounded-md px-2 bg-white" value={editing.role || "Installer"} onChange={(e) => setEditing({ ...editing, role: e.target.value, is_lead: ["Foreman", "Lead"].includes(e.target.value) })}>
                  {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select></div>
              <div><label className="text-xs text-gray-600">Crew</label>
                <select data-testid="inst-edit-crew" className="h-9 w-full text-sm border border-gray-300 rounded-md px-2 bg-white" value={editing.crew_id || ""} onChange={(e) => setEditing({ ...editing, crew_id: e.target.value })}>
                  <option value="">— No crew —</option>
                  {crews.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select></div>
              <div className="col-span-2 flex items-center gap-2 pt-1">
                <input
                  type="checkbox"
                  id="inst-edit-team-toggle"
                  data-testid="inst-edit-team-toggle"
                  checked={!!editing.is_teamed}
                  onChange={(e) => setEditing({ ...editing, is_teamed: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="inst-edit-team-toggle" className="text-sm font-medium text-gray-800 cursor-pointer">
                  Team member
                  <span className="text-xs text-gray-500 ml-1 font-normal">— pair this installer with a teammate (managed in Settings → Teams)</span>
                </label>
              </div>
              <div>
                <label className="text-xs text-gray-600">Home region</label>
                {editing._addingRegion ? (
                  <div className="flex items-center gap-1">
                    <Input
                      autoFocus
                      data-testid="inst-edit-region-new"
                      value={editing._newRegion || ""}
                      onChange={(e) => setEditing({ ...editing, _newRegion: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const v = (editing._newRegion || "").trim();
                          if (v) setEditing({ ...editing, home_region: v, _addingRegion: false, _newRegion: "" });
                        } else if (e.key === "Escape") {
                          setEditing({ ...editing, _addingRegion: false, _newRegion: "" });
                        }
                      }}
                      placeholder="New region name"
                      className="h-9 flex-1"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-9"
                      onClick={() => {
                        const v = (editing._newRegion || "").trim();
                        if (v) setEditing({ ...editing, home_region: v, _addingRegion: false, _newRegion: "" });
                      }}
                    >Add</Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-9"
                      onClick={() => setEditing({ ...editing, _addingRegion: false, _newRegion: "" })}
                    >Cancel</Button>
                  </div>
                ) : (
                  <select
                    data-testid="inst-edit-region"
                    className="h-9 w-full text-sm border border-gray-300 rounded-md px-2 bg-white"
                    value={editing.home_region || ""}
                    onChange={(e) => {
                      if (e.target.value === "__add__") {
                        setEditing({ ...editing, _addingRegion: true, _newRegion: "" });
                      } else {
                        setEditing({ ...editing, home_region: e.target.value });
                      }
                    }}
                  >
                    <option value="">—</option>
                    {regionOptions.map((r) => <option key={r} value={r}>{r}</option>)}
                    <option value="__add__">+ Add home region…</option>
                  </select>
                )}
              </div>
              <div><label className="text-xs text-gray-600">Email</label>
                <Input value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></div>
              <div><label className="text-xs text-gray-600">Phone</label>
                <Input
                  data-testid="inst-edit-phone"
                  value={editing.phone || ""}
                  onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                  onBlur={(e) => {
                    const formatted = formatPhone(e.target.value);
                    if (formatted !== e.target.value) setEditing((prev) => ({ ...prev, phone: formatted }));
                  }}
                  placeholder="(303) 313-3333"
                /></div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600">Address (used for distance-to-jobsite scoring)</label>
                <Input data-testid="inst-edit-address" value={editing.address || ""} onChange={(e) => setEditing({ ...editing, address: e.target.value })} placeholder="123 Main St, Denver, CO 80202" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600">Standing assignment (auto-fill on empty days)</label>
                <Input data-testid="inst-edit-standing" value={editing.standing_assignment || ""} onChange={(e) => setEditing({ ...editing, standing_assignment: e.target.value })} placeholder="e.g. 9998 Warehouse, **SERVICE-OPW**" /></div>

              {/* Skills with 3-state scoring */}
              <div className="col-span-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-600">Skills <span className="text-gray-400">· click once = partial (50), twice = competent (100), third = clear</span></label>
                  <div className="text-[10px] text-gray-500 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-orange-400"></span>50</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500"></span>100</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 p-2 border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                  {scopes.map((s) => {
                    const score = (editing.skill_scores || {})[s.code] || 0;
                    const color = score === 100 ? "bg-emerald-500 text-white border-emerald-600" : score === 50 ? "bg-orange-400 text-white border-orange-500" : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100";
                    return (
                      <button
                        type="button"
                        key={s.id}
                        data-testid={`inst-skill-${s.code}`}
                        onClick={() => cycleSkill(s.code)}
                        className={`inline-flex items-center gap-1 text-xs border rounded px-1.5 py-0.5 transition-colors ${color}`}
                        title={`${s.trade} — score ${score}`}
                      >
                        <span className="font-mono text-[10px] opacity-80">{s.code}</span>
                        <span>{s.trade}</span>
                        {score > 0 && <span className="ml-0.5 font-bold">{score}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Badges — multi-select by project + custom facility */}
              <div className="col-span-2">
                <label className="text-xs text-gray-600">Badges <span className="text-gray-400">· tied to a job or facility</span></label>
                <div className="border border-gray-200 rounded-md p-2 space-y-2">
                  {(editing.badges || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5" data-testid="inst-edit-badges-list">
                      {(editing.badges || []).map((b) => (
                        <span key={b} className="inline-flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                          {badgeLabel(b)}
                          <button type="button" onClick={() => removeBadge(b)} className="hover:text-red-600" data-testid={`inst-edit-badge-remove-${b}`}>×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <select
                      data-testid="inst-edit-badge-project"
                      className="h-8 text-xs border border-gray-300 rounded-md px-2 bg-white flex-1"
                      value=""
                      onChange={(e) => { if (e.target.value) addBadge(`proj:${e.target.value}`); }}
                    >
                      <option value="">+ Add job badge…</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.project_name}{p.job_number ? ` (#${p.job_number})` : ""}
                        </option>
                      ))}
                    </select>
                    <Input
                      data-testid="inst-edit-badge-facility"
                      placeholder="or facility (e.g. DIA)"
                      value={newBadge}
                      onChange={(e) => setNewBadge(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && newBadge.trim()) { e.preventDefault(); addBadge(`facility:${newBadge.trim()}`); } }}
                      className="h-8 text-xs flex-1"
                    />
                    <Button size="sm" variant="outline" onClick={() => newBadge.trim() && addBadge(`facility:${newBadge.trim()}`)} data-testid="inst-edit-badge-add">Add</Button>
                  </div>
                </div>
              </div>

              <div className="col-span-2 flex flex-wrap gap-4 border-t border-gray-100 pt-3">
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="inst-edit-drug" checked={!!editing.drug_test_cleared} onChange={(e) => setEditing({ ...editing, drug_test_cleared: e.target.checked })} /> Drug Free
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="inst-edit-active" checked={editing.active !== false} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} /> Active
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer" title="When checked, this installer is excluded from auto-email dispatch.">
                  <input type="checkbox" data-testid="inst-edit-email-opt-out" checked={!!editing.email_opt_out} onChange={(e) => setEditing({ ...editing, email_opt_out: e.target.checked })} /> Email opt-out
                </label>
              </div>
              <div><label className="text-xs text-gray-600">Vehicle</label>
                <Input value={editing.vehicle || ""} onChange={(e) => setEditing({ ...editing, vehicle: e.target.value })} /></div>
              <div><label className="text-xs text-gray-600">Max hrs / day</label>
                <Input type="number" step="0.5" value={editing.max_hours_per_day || 10} onChange={(e) => setEditing({ ...editing, max_hours_per_day: Number(e.target.value) })} /></div>
              <div><label className="text-xs text-gray-600">Hire date</label>
                <Input type="date" value={editing.hire_date || ""} onChange={(e) => setEditing({ ...editing, hire_date: e.target.value })} /></div>
              <div><label className="text-xs text-gray-600">Emergency contact</label>
                <Input value={editing.emergency_contact || ""} onChange={(e) => setEditing({ ...editing, emergency_contact: e.target.value })} /></div>
              <div className="col-span-2">
                <label className="text-xs text-gray-600">Notes</label>
                <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>
            <footer className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="" onClick={save} data-testid="inst-edit-save"><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
            </footer>
          </div>
        </div>
      )}

      {/* ── Duplicate Installers dialog ──────────────────────────────── */}
      {dupesOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDupesOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="installer-dupes-dialog">
            <header className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold">Find Duplicate Installers</h3>
                <p className="text-[11px] text-gray-500 mt-0.5">Same name (case-insensitive). Older record is kept; newer one is merged + archived.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDupesOpen(false)}>Close</Button>
            </header>
            <div className="p-4 space-y-3">
              {dupesLoading ? (
                <p className="text-center text-sm text-gray-500 py-8">Scanning…</p>
              ) : dupeGroups.length === 0 ? (
                <p className="text-center text-sm text-gray-500 py-8">✓ No duplicate installers found.</p>
              ) : (
                dupeGroups.map((g) => {
                  const keep = g.installers[0];
                  const others = g.installers.slice(1);
                  return (
                    <div key={g.key} className="border border-gray-200 rounded-md overflow-hidden" data-testid={`dupe-group-${g.key.replace(/\s+/g, "-")}`}>
                      <header className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs font-semibold text-amber-800">
                        Duplicate: {g.installers[0].name} <span className="font-normal text-amber-700">· {g.installers.length} rows</span>
                      </header>
                      <div className="divide-y divide-gray-100">
                        <div className="px-3 py-2 flex items-start gap-3 bg-emerald-50/30" data-testid={`dupe-keep-${keep.id}`}>
                          <span className="inline-flex items-center text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5 mt-0.5">KEEP</span>
                          <div className="flex-1 text-xs text-gray-700">
                            <div className="font-medium text-gray-900">{keep.name} <span className="text-gray-400">· {keep.role || "Installer"}</span></div>
                            <div className="text-[10px] text-gray-500 font-mono">{keep.id.slice(0, 8)} · created {(keep.created_at || "").slice(0, 10)}</div>
                            <div className="text-[10px] text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                              {keep.email && <span>📧 {keep.email}</span>}
                              {keep.phone && <span>📞 {keep.phone}</span>}
                              {keep.employee_id && <span>ID {keep.employee_id}</span>}
                              {keep.crew_id && <span>crew ✓</span>}
                              <span>active: {keep.active === false ? "no" : "yes"}</span>
                            </div>
                          </div>
                        </div>
                        {others.map((dr) => (
                          <div key={dr.id} className="px-3 py-2 flex items-start gap-3" data-testid={`dupe-drop-${dr.id}`}>
                            <span className="inline-flex items-center text-[10px] font-bold uppercase bg-red-50 text-red-700 border border-red-200 rounded px-1.5 py-0.5 mt-0.5">MERGE</span>
                            <div className="flex-1 text-xs text-gray-700">
                              <div className="font-medium text-gray-900">{dr.name} <span className="text-gray-400">· {dr.role || "Installer"}</span></div>
                              <div className="text-[10px] text-gray-500 font-mono">{dr.id.slice(0, 8)} · created {(dr.created_at || "").slice(0, 10)}</div>
                              <div className="text-[10px] text-gray-600 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                                {dr.email && <span>📧 {dr.email}</span>}
                                {dr.phone && <span>📞 {dr.phone}</span>}
                                {dr.employee_id && <span>ID {dr.employee_id}</span>}
                                {dr.crew_id && <span>crew ✓</span>}
                                <span>active: {dr.active === false ? "no" : "yes"}</span>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              data-testid={`dupe-merge-btn-${dr.id}`}
                              onClick={() => mergeDupes(keep.id, dr.id)}
                              className="shrink-0"
                            >
                              Merge → Keep older
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Crews sub-tab ----------------
function CrewsSection() {
  const [crews, setCrews] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [editingMembers, setEditingMembers] = useState(null); // crew.id when open
  const load = async () => {
    const [a, b] = await Promise.all([api.get("/crews"), api.get("/installers")]);
    setCrews(a.data || []);
    setInstallers(b.data || []);
  };
  useEffect(() => { load(); }, []);

  const saveColor = async (id, color) => {
    await api.patch(`/crews/${id}`, { color });
    setCrews((c) => c.map((x) => (x.id === id ? { ...x, color } : x)));
  };
  const saveName = async (id, name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) { toast.error("Crew name is required"); return; }
    try {
      const r = await api.patch(`/crews/${id}`, { name: trimmed });
      setCrews((c) => c.map((x) => (x.id === id ? r.data : x)));
      toast.success("Crew renamed");
    } catch { toast.error("Could not rename crew"); }
  };
  const saveForeman = async (id, foreman_id) => {
    const r = await api.patch(`/crews/${id}`, { foreman_id });
    setCrews((c) => c.map((x) => (x.id === id ? r.data : x)));
    // Foreman cascade may have added them to members AND set their crew_id —
    // refresh installers so the table re-paints.
    const inst = await api.get("/installers");
    setInstallers(inst.data || []);
    toast.success("Foreman set");
  };

  const toggleMember = async (crew, installerId) => {
    const set = new Set(crew.member_installer_ids || []);
    if (set.has(installerId)) set.delete(installerId);
    else set.add(installerId);
    const next = Array.from(set);
    try {
      const r = await api.patch(`/crews/${crew.id}`, { member_installer_ids: next });
      setCrews((cs) => cs.map((x) => (x.id === crew.id ? r.data : x)));
      // Pull installer list to reflect cascaded crew_id changes.
      const inst = await api.get("/installers");
      setInstallers(inst.data || []);
    } catch { toast.error("Could not update members"); }
  };

  const installerById = useMemo(() => Object.fromEntries(installers.map((i) => [i.id, i])), [installers]);
  const activeInstallers = useMemo(() => installers.filter((i) => i.active !== false).sort((a, b) => a.name.localeCompare(b.name)), [installers]);

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr className="text-xs text-gray-500 uppercase tracking-wider">
            <th className="text-left px-3 py-2 font-medium">Crew</th>
            <th className="text-left px-3 py-2 font-medium">Color</th>
            <th className="text-left px-3 py-2 font-medium">Foreman / Lead</th>
            <th className="text-left px-3 py-2 font-medium">Members</th>
          </tr>
        </thead>
        <tbody>
          {crews.map((c) => {
            const memberIds = c.member_installer_ids || [];
            return (
              <React.Fragment key={c.id}>
                <tr className="border-t border-gray-100">
                  <td className="px-3 py-2 font-medium text-gray-900">
                    <Input
                      data-testid={`crew-name-${c.id}`}
                      defaultValue={c.name}
                      onBlur={(e) => {
                        const v = e.target.value.trim();
                        if (v && v !== c.name) saveName(c.id, v);
                        else e.target.value = c.name; // revert empty/no-op
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.currentTarget.blur(); }
                        else if (e.key === "Escape") { e.currentTarget.value = c.name; e.currentTarget.blur(); }
                      }}
                      className="h-8 text-sm font-medium"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="color"
                      value={c.color || "#999999"}
                      onChange={(e) => saveColor(c.id, e.target.value)}
                      data-testid={`crew-color-${c.id}`}
                      className="w-10 h-7 border border-gray-200 rounded cursor-pointer"
                    />
                    <span className="ml-2 text-xs font-mono text-gray-500">{c.color}</span>
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="h-8 text-sm border border-gray-300 rounded-md px-2 bg-white"
                      value={c.foreman_id || ""}
                      onChange={(e) => saveForeman(c.id, e.target.value)}
                      data-testid={`crew-foreman-${c.id}`}
                    >
                      <option value="">—</option>
                      {activeInstallers.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.name} {i.role && i.role !== "Installer" ? `· ${i.role}` : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div className="flex items-start gap-2 flex-wrap">
                      <div className="text-xs text-gray-700 flex-1 min-w-0">
                        {memberIds.length === 0 && <span className="text-gray-400 italic">No members</span>}
                        {memberIds.map((id) => {
                          const inst = installerById[id];
                          if (!inst) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-1 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5 text-[11px] mr-1 mb-1"
                              data-testid={`crew-member-pill-${c.id}-${id}`}
                            >
                              {inst.name}
                              {c.foreman_id === id && <span className="text-[9px] text-purple-600 font-semibold uppercase">Lead</span>}
                            </span>
                          );
                        })}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingMembers(editingMembers === c.id ? null : c.id)}
                        data-testid={`crew-edit-members-${c.id}`}
                      >
                        {editingMembers === c.id ? "Done" : "Edit"}
                      </Button>
                    </div>
                  </td>
                </tr>
                {editingMembers === c.id && (
                  <tr className="bg-blue-50/30">
                    <td colSpan={4} className="px-3 py-3 border-t border-blue-100">
                      <div className="text-[11px] uppercase font-semibold text-gray-600 mb-2">
                        Toggle members in <span className="text-gray-900">{c.name}</span> · changes sync to each installer's crew assignment instantly
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1">
                        {activeInstallers.map((i) => {
                          const inThisCrew = (c.member_installer_ids || []).includes(i.id);
                          const inOtherCrew = !inThisCrew && i.crew_id && i.crew_id !== c.id;
                          const otherCrewName = inOtherCrew ? crews.find((x) => x.id === i.crew_id)?.name : null;
                          return (
                            <label
                              key={i.id}
                              className={`flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer border ${
                                inThisCrew
                                  ? "bg-white border-blue-300 text-gray-900 font-medium"
                                  : inOtherCrew
                                  ? "bg-amber-50 border-amber-200 text-gray-700 hover:bg-amber-100"
                                  : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                              }`}
                              data-testid={`crew-member-toggle-${c.id}-${i.id}`}
                            >
                              <input
                                type="checkbox"
                                checked={inThisCrew}
                                onChange={() => toggleMember(c, i.id)}
                                className="w-3.5 h-3.5 accent-blue-600"
                              />
                              <span className="flex-1 truncate">{i.name}</span>
                              {inOtherCrew && (
                                <span className="text-[9px] text-amber-700 truncate" title={`Currently in ${otherCrewName}`}>
                                  in {otherCrewName}
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-gray-500 mt-2">
                        Adding an installer who's currently in another crew will move them — they can only belong to one crew.
                      </p>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------- Teams sub-tab (pair-based teaming) ----------------
// Surfaces only installers with `is_teamed=True`. Each row pairs to ANOTHER
// teamed installer; the dropdown is the canonical place to set the pair.
// Saving a pair mirrors both sides server-side (see update_installer cascade).
function TeamsSection() {
  const [installers, setInstallers] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/installers");
      setInstallers((r.data || []).filter((i) => i.active));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const teamed = useMemo(
    () => installers.filter((i) => i.is_teamed).sort((a, b) => a.name.localeCompare(b.name)),
    [installers],
  );
  // Group rows into TEAMS — each pair shows up as one block. Unpaired
  // teamed installers fall into a tail group.
  const groups = useMemo(() => {
    const seen = new Set();
    const teams = [];
    for (const i of teamed) {
      if (seen.has(i.id)) continue;
      const partner = i.teammate_id ? teamed.find((x) => x.id === i.teammate_id) : null;
      // Mutual link only — a real "team" requires both sides to point at
      // each other. One-sided links land in the unpaired tail.
      if (partner && partner.teammate_id === i.id) {
        teams.push({ kind: "team", members: [i, partner] });
        seen.add(i.id);
        seen.add(partner.id);
      }
    }
    const unpaired = teamed.filter((i) => !seen.has(i.id));
    return { teams, unpaired };
  }, [teamed]);
  const partnerOptions = (currentId) => teamed.filter((i) => i.id !== currentId);

  const setPartner = async (id, partnerId) => {
    try {
      await api.patch(`/installers/${id}`, { teammate_id: partnerId });
      // Mirror the link on the other installer too — if the picker chose
      // someone, set the reverse link; if cleared, also clear theirs if it
      // was pointing back at us.
      if (partnerId) {
        await api.patch(`/installers/${partnerId}`, { teammate_id: id });
      } else {
        const cur = teamed.find((x) => x.id === id);
        if (cur?.teammate_id) {
          const other = teamed.find((x) => x.id === cur.teammate_id);
          if (other && other.teammate_id === id) {
            await api.patch(`/installers/${other.id}`, { teammate_id: "" });
          }
        }
      }
      toast.success(partnerId ? "Team paired" : "Team unpaired");
      load();
    } catch { toast.error("Update failed"); }
  };

  const untoggle = async (i) => {
    try {
      await api.patch(`/installers/${i.id}`, { is_teamed: false });
      toast.success(`${i.name} removed from Teams`);
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="settings-teams-section">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-sm text-gray-600">
          {groups.teams.length} team{groups.teams.length === 1 ? "" : "s"} · {teamed.length} installer{teamed.length === 1 ? "" : "s"} marked
        </span>
        <span className="ml-auto text-xs text-gray-400">Mark "Team member" on an installer → assign their pair here</span>
      </div>

      {loading && <div className="px-3 py-6 text-center text-gray-500 border border-gray-200 rounded-md bg-white">Loading…</div>}
      {!loading && teamed.length === 0 && (
        <div className="px-3 py-12 text-center text-gray-500 border border-gray-200 rounded-md bg-white">
          <Link2 className="w-6 h-6 mx-auto text-gray-400 mb-2" strokeWidth={1.5} />
          No team members yet. Open an installer in the Installers tab and check <strong>"Team member"</strong> to add them here.
        </div>
      )}

      {/* Paired teams — each group rendered as a single block. */}
      {groups.teams.length > 0 && (
        <div className="grid grid-cols-1 gap-3 mb-4">
          {groups.teams.map(({ members }) => (
            <div
              key={members.map((m) => m.id).join("+")}
              className="border border-emerald-200 bg-emerald-50/40 rounded-lg overflow-hidden"
              data-testid={`teams-group-${members[0].id}`}
            >
              <div className="px-3 py-1.5 bg-emerald-100/70 text-[11px] uppercase tracking-wider font-semibold text-emerald-800 flex items-center gap-1.5">
                <Link2 className="w-3 h-3" />
                Team · {members.map((m) => m.name.split(" ")[0]).join(" + ")}
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {members.map((i) => {
                    const crewName = (installers.find((x) => x.id === i.crew_id) || {}).name || "—";
                    return (
                      <tr key={i.id} className="border-t border-emerald-100 hover:bg-emerald-50/60" data-testid={`teams-row-${i.id}`}>
                        <td className="px-3 py-1.5 font-medium text-gray-900 w-1/3 truncate">{i.name}</td>
                        <td className="px-3 py-1.5 text-gray-600 text-xs">{i.role || "—"}</td>
                        <td className="px-3 py-1.5 text-gray-600 text-xs truncate">{crewName}</td>
                        <td className="px-3 py-1.5 text-right w-10">
                          <button
                            onClick={() => untoggle(i)}
                            className="text-gray-400 hover:text-red-600"
                            title="Remove from Teams"
                            data-testid={`teams-remove-${i.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Unpaired teamed installers — show partner picker so they can be linked. */}
      {groups.unpaired.length > 0 && (
        <div className="border border-amber-200 bg-amber-50/40 rounded-lg overflow-hidden">
          <div className="px-3 py-1.5 bg-amber-100/70 text-[11px] uppercase tracking-wider font-semibold text-amber-800">
            Unpaired ({groups.unpaired.length}) — pick a partner to form a team
          </div>
          <table className="w-full text-sm">
            <tbody>
              {groups.unpaired.map((i) => (
                <tr key={i.id} className="border-t border-amber-100 hover:bg-amber-50/60" data-testid={`teams-row-${i.id}`}>
                  <td className="px-3 py-1.5 font-medium text-gray-900 w-1/4 truncate">{i.name}</td>
                  <td className="px-3 py-1.5 text-gray-600 text-xs w-24">{i.role || "—"}</td>
                  <td className="px-3 py-1.5">
                    <select
                      data-testid={`teams-partner-${i.id}`}
                      className="h-8 text-sm border border-gray-300 rounded-md px-2 bg-white min-w-[200px]"
                      value={i.teammate_id || ""}
                      onChange={(e) => setPartner(i.id, e.target.value)}
                    >
                      <option value="">— Pick a partner —</option>
                      {partnerOptions(i.id).map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}{p.teammate_id && p.teammate_id !== i.id ? ` (currently with ${(teamed.find((x) => x.id === p.teammate_id) || {}).name || "?"})` : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-1.5 text-right w-10">
                    <button
                      onClick={() => untoggle(i)}
                      className="text-gray-400 hover:text-red-600"
                      title="Remove from Teams"
                      data-testid={`teams-remove-${i.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------- Page ----------------
function RoleSection() {
  // Role comes from the host account (Settings > Users & Roles) — read-only here.
  const { role } = useRole();
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="settings-role-section">
      <h3 className="text-sm font-semibold text-gray-900">Active role</h3>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">Your role comes from your ABS account and is managed in Settings &gt; Users &amp; Roles.</p>
      <span className="inline-flex px-3 h-8 items-center text-sm rounded-md border bg-blue-600 text-white border-blue-600" data-testid="settings-current-role">
        {role}
      </span>
    </section>
  );
}

// Global labor math settings — used by every duration calculation.
function LaborDefaultsSection() {
  const [settings, setSettings] = useState(null);
  const [factor, setFactor] = useState("0.8");
  useEffect(() => {
    api.get("/settings/app").then((r) => {
      setSettings(r.data);
      setFactor(String(r.data.duration_budget_factor ?? 0.8));
    }).catch(() => {});
  }, []);
  const save = async (patch, label = "Labor defaults saved") => {
    try {
      const r = await api.patch("/settings/app", patch);
      setSettings(r.data);
      setFactor(String(r.data.duration_budget_factor));
      toast.success(label);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  if (!settings) return null;
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="settings-labor-section">
      <h3 className="text-sm font-semibold text-gray-900">Labor defaults</h3>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">
        Duration = ceil( budgeted hours × factor ÷ (crew size × hours per day) ). Used everywhere durations are shown.
      </p>
      <div className="flex flex-wrap items-end gap-6">
        <div>
          <span className="block text-xs text-gray-500 mb-1">Working hours per day</span>
          <div className="flex gap-2">
            {[8, 10].map((h) => (
              <button
                key={h}
                data-testid={`settings-hours-per-day-${h}`}
                onClick={() => save({ hours_per_day: h })}
                className={`px-3 h-8 text-sm rounded-md border transition-colors ${
                  settings.hours_per_day === h
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {h} hrs
              </button>
            ))}
          </div>
        </div>
        <label className="text-xs text-gray-500">
          <span className="block mb-1">Auto-request window (weeks)</span>
          <input
            data-testid="settings-auto-request-weeks"
            type="number"
            min="0"
            max="104"
            value={settings.auto_request_window_weeks ?? 6}
            onChange={(e) => setSettings({ ...settings, auto_request_window_weeks: e.target.value })}
            onBlur={(e) => Number(e.target.value) !== settings.auto_request_window_weeks && save({ auto_request_window_weeks: Number(e.target.value) })}
            className="h-8 w-24 border border-gray-300 rounded-md px-2 text-sm bg-white"
          />
        </label>
        <label className="text-xs text-gray-500">
          <span className="block mb-1">Material red/amber horizon (days)</span>
          <input
            data-testid="settings-material-horizon"
            type="number"
            min="0"
            max="90"
            value={settings.material_scheduled_horizon_days ?? 7}
            onChange={(e) => setSettings({ ...settings, material_scheduled_horizon_days: e.target.value })}
            onBlur={(e) => Number(e.target.value) !== settings.material_scheduled_horizon_days && save({ material_scheduled_horizon_days: Number(e.target.value) })}
            className="h-8 w-24 border border-gray-300 rounded-md px-2 text-sm bg-white"
          />
          <span className="block text-[11px] text-gray-400">
            a room scheduled inside this window turns red when anything is not on hand or not ordered
          </span>
        </label>
        <label className="text-xs text-gray-500">
          <span className="block mb-1">Shortfall digest hour (Mon–Fri, Denver)</span>
          <input
            data-testid="settings-digest-hour"
            type="number"
            min="0"
            max="23"
            value={settings.shortfall_digest_hour ?? 7}
            onChange={(e) => setSettings({ ...settings, shortfall_digest_hour: e.target.value })}
            onBlur={(e) => Number(e.target.value) !== settings.shortfall_digest_hour && save({ shortfall_digest_hour: Number(e.target.value) })}
            className="h-8 w-24 border border-gray-300 rounded-md px-2 text-sm bg-white"
          />
        </label>
        <label className="text-xs text-gray-500 flex items-center gap-2 pb-1">
          <input
            data-testid="settings-drawings-upload-verification"
            type="checkbox"
            checked={settings.drawings_upload_verification !== false}
            onChange={(e) => save({ drawings_upload_verification: e.target.checked })}
          />
          <span>
            Confirm sheet numbers after a drawing upload
            <span className="block text-[11px] text-gray-400">
              on = after the set is read you see a thumbnail per page with the sheet number and
              title it found, and correct anything wrong before the set goes current
            </span>
          </span>
        </label>
        <label className="text-xs text-gray-500 flex items-center gap-2 pb-1">
          <input
            data-testid="settings-material-alert-send"
            type="checkbox"
            checked={!!settings.material_alert_send}
            onChange={(e) => save({ material_alert_send: e.target.checked })}
          />
          <span>
            Email the material alerts
            <span className="block text-[11px] text-gray-400">
              off = arrivals, follow-up tickets and no-PO lines are still logged and still show as
              Priority Actions, they are just not emailed to the foreman or PM
            </span>
          </span>
        </label>
        <label className="text-xs text-gray-500 flex items-center gap-2 pb-1">
          <input
            data-testid="settings-digest-send"
            type="checkbox"
            checked={!!settings.shortfall_digest_send}
            onChange={(e) => save({ shortfall_digest_send: e.target.checked })}
          />
          <span>
            Email the shortfall digest
            <span className="block text-[11px] text-gray-400">
              off = the digest is still built and logged every weekday, just not sent
            </span>
          </span>
        </label>
        <label className="text-xs text-gray-500 col-span-2 w-full">
          <span className="block mb-1">
            Punchlist “responsible party” synonyms — one per line, rows matching any of these are ours
          </span>
          <textarea
            data-testid="settings-abs-synonyms"
            rows={7}
            // Keyed so it re-mounts once settings arrive: an uncontrolled textarea that mounted
            // empty would blur an EMPTY list over the saved one and wipe the synonyms.
            key={`syn-${(settings.abs_synonyms || []).length}`}
            defaultValue={(settings.abs_synonyms || []).join("\n")}
            onBlur={(e) => {
              const list = e.target.value.split("\n").map((x) => x.trim()).filter(Boolean);
              const current = settings.abs_synonyms || [];
              if (!list.length && current.length) return;   // never clear the list by accident
              if (JSON.stringify(list) !== JSON.stringify(current)) {
                save({ abs_synonyms: list }, "Punchlist synonyms saved");
              }
            }}
            placeholder={"ABS\nABS, Inc\nAmerican Building Supply\nToilet Accessories"}
            className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white leading-5"
          />
        </label>
        <label className="text-xs text-gray-500">
          <span className="block mb-1">Duration budget factor</span>
          <input
            data-testid="settings-duration-factor"
            type="number"
            step="0.05"
            min="0.05"
            max="2"
            value={factor}
            onChange={(e) => setFactor(e.target.value)}
            onBlur={() => Number(factor) !== settings.duration_budget_factor && save({ duration_budget_factor: Number(factor) })}
            className="h-8 w-24 border border-gray-300 rounded-md px-2 text-sm bg-white"
          />
        </label>
      </div>
    </section>
  );
}

// TIME-1 §5 — timekeeping. The master switch ships OFF and the rules below are conservative on
// purpose: they need an attorney's review before anybody is clocked in against them.
const TIME_RULES = [
  ["timekeeping_rounding_minutes", "Rounding (minutes)", "0 = to the minute, which is the safe default"],
  ["timekeeping_max_shift_hours", "Long-shift warning (hours)", "raises a flag, never a deduction"],
  ["timekeeping_break_required_after_hours", "Break required after (hours)", "state rule — check yours"],
  ["timekeeping_break_minutes", "Break length (minutes)", "a missed break is FLAGGED, never auto-deducted"],
  ["timekeeping_day_overtime_hours", "Daily overtime flag (hours)", "0 = off. Miter computes pay"],
  ["timekeeping_week_overtime_hours", "Weekly overtime flag (hours)", "a flag for a human to look at"],
  ["timekeeping_geofence_radius_m", "Site fence radius (m)", "per job override available"],
  ["timekeeping_off_site_tolerance_minutes", "Off-site tolerance (minutes)", "before it is flagged"],
  ["timekeeping_window_tolerance_minutes", "Early/late tolerance (minutes)", "vs the dispatch window"],
  ["timekeeping_breadcrumb_interval_minutes", "Breadcrumb interval (minutes)", "only while clocked in"],
  ["timekeeping_repeat_flag_threshold", "Repeat-flag threshold", "per employee, before the PM is told"],
  ["timekeeping_retention_years", "Keep time records (years)", "nothing is deleted before this"],
];

function TimekeepingSection() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings/app").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  const save = async (patch, label = "Timekeeping settings saved") => {
    try {
      const r = await api.patch("/settings/app", patch);
      setSettings(r.data); invalidateAppSettings(); toast.success(label);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  if (!settings) return null;
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="settings-timekeeping-section">
      <h3 className="text-sm font-semibold text-gray-900">Timekeeping</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        Clock in and out in the app, with the site fence kept as evidence. Miter stays the payroll
        system of record — this app never calculates pay.
      </p>
      <p className="mt-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-2"
         data-testid="settings-timekeeping-attorney">
        <b>Have an attorney review these rules before the master switch is turned on.</b> Rounding,
        break rules and overtime thresholds are wage-and-hour law and they differ by state. The
        defaults here are deliberately conservative: no rounding, no automatic break deduction, and
        overtime shown only as a flag.
      </p>
      <div className="mt-3 space-y-2">
        {[["timekeeping_enabled", "Timekeeping master switch",
           "off = no punches, no location, nothing recorded. Each employee must also accept the location notice before it applies to them."],
          ["clockin_safety_prompts", "Safety prompt at clock-in",
           "the trade's question set, a couple of statements a day, picked at random per person and answered before the punch is recorded"],
          ["clockout_material_questions", "Material questions at clock-out",
           "the trade's clock-out set — what moved, what ran short, what is in somebody's way tonight; answers land on the time entry"],
          ["timekeeping_photo_at_punch", "Photo at punch", "optional; stored with the punch"]].map(
          ([key, label, hint]) => (
            <label key={key} className="text-xs text-gray-500 flex items-start gap-2">
              <input type="checkbox" className="mt-0.5" data-testid={`settings-${key.replace(/_/g, "-")}`}
                     checked={!!settings[key]}
                     onChange={(e) => save({ [key]: e.target.checked })} />
              <span>
                {label}
                <span className="block text-[11px] text-gray-400">{hint}</span>
              </span>
            </label>
          ))}
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {TIME_RULES.map(([key, label, hint]) => (
          <label key={key} className="text-[11px] text-gray-500">
            {label}
            <Input type="number" defaultValue={settings[key]} className="h-8 text-xs mt-0.5"
                   data-testid={`settings-${key.replace(/_/g, "-")}`}
                   onBlur={(e) => Number(e.target.value) !== Number(settings[key])
                     && save({ [key]: Number(e.target.value) }, `${label} saved`)} />
            <span className="block text-gray-400">{hint}</span>
          </label>
        ))}
      </div>
      <div className="mt-3">
        <span className="block text-xs text-gray-500 mb-1">
          Legacy safety lines ({(settings.clockin_safety_questions || []).length}) — used only
          while no question set exists, one per line
        </span>
        <Textarea rows={3} className="text-xs" data-testid="settings-safety-questions"
                  defaultValue={(settings.clockin_safety_questions || []).join("\n")}
                  onBlur={(e) => save({
                    clockin_safety_questions: e.target.value.split("\n")
                      .map((x) => x.trim()).filter(Boolean),
                  }, "Safety questions saved")} />
      </div>
      <QuestionSetsPanel />
      <TakeoffSettings />
    </section>
  );
}

// FT-2 — how the field views open and behave. Everything here ships OFF / neutral.
function FieldViewsSection() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings/app").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  const save = async (patch, label = "Field view settings saved") => {
    try {
      const r = await api.patch("/settings/app", patch);
      setSettings(r.data);
      invalidateAppSettings();
      toast.success(label);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  if (!settings) return null;
  const view = settings.install_default_view || "last_project";
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="settings-field-views-section">
      <h3 className="text-sm font-semibold text-gray-900">Field views</h3>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">
        How the Install view opens, and whether the crew is asked for a photo when material lands.
      </p>
      <div className="space-y-3">
        <div>
          <span className="block text-xs text-gray-500 mb-1">Install view opens on</span>
          <div className="flex gap-2 flex-wrap">
            {[["dispatch", "Today's dispatch"], ["last_project", "The last job worked"]].map(([v, label]) => (
              <button
                key={v}
                data-testid={`settings-install-default-view-${v}`}
                onClick={() => save({ install_default_view: v })}
                className={`px-3 h-8 text-sm rounded-md border transition-colors ${
                  view === v ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
              >
                {label}
              </button>
            ))}
          </div>
          <span className="block text-[11px] text-gray-400 mt-1">
            Today's dispatch opens the job, building, floor and area on the installer's dispatch
            card for today or this week, as a filter they can clear in one tap. Neither option ever
            opens an arbitrary first job.
          </span>
        </div>
        <label className="text-xs text-gray-500 flex items-start gap-2">
          <input
            data-testid="settings-material-placement-photos"
            type="checkbox"
            className="mt-0.5"
            checked={!!settings.material_placement_photos}
            onChange={(e) => save({ material_placement_photos: e.target.checked })}
          />
          <span>
            Placement photos on material
            <span className="block text-[11px] text-gray-400">
              on = a delivery or a move can attach a photo to the material record; the photo travels
              with the material and always names the location it was taken at. Photos queue offline
              and upload when there is signal.
            </span>
          </span>
        </label>
      </div>
    </section>
  );
}

// Per-user display preferences (stored per browser profile alongside the
// existing role / theme / sidebar preferences).
function PreferencesSection() {
  const [frsView, setFrsView] = useState(
    () => (localStorage.getItem("fieldops-frs-default-view") === "tree" ? "tree" : "grid"),
  );
  const [drawTarget, setDrawTarget] = useState(() => linkTarget());
  const onChange = (v) => {
    setFrsView(v);
    localStorage.setItem("fieldops-frs-default-view", v);
    toast.success(`Default FRS view · ${v === "tree" ? "Tree" : "Grid"}`);
  };
  const onTarget = (v) => {
    setDrawTarget(v);
    setLinkTarget(v);
    toast.success(`Drawing links · ${v === "new" ? "new tab" : "same tab"}`);
  };
  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4 mb-4" data-testid="settings-preferences-section">
      <h3 className="text-sm font-semibold text-gray-900">My preferences</h3>
      <p className="text-xs text-gray-500 mt-0.5 mb-3">Default FRS view — which view opens when you enter a job's FRS.</p>
      <div className="flex items-center gap-2 flex-wrap">
        {[["grid", "Grid"], ["tree", "Tree"]].map(([v, label]) => (
          <button
            key={v}
            data-testid={`settings-frs-view-${v}`}
            onClick={() => onChange(v)}
            className={`px-3 h-8 text-sm rounded-md border transition-colors ${
              frsView === v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {/* DRW-LINKS — one setting for every drawing link on a computer. A phone always opens the
          drawing in place and shows a Back bar, because there is no second tab to return to. */}
      <p className="text-xs text-gray-500 mt-4 mb-3">
        Drawing links on a computer — a plan link from FRS, Install, Material or Picking.
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        {[["new", "Open in a new tab"], ["same", "Open in this tab"]].map(([v, label]) => (
          <button
            key={v}
            data-testid={`settings-drawing-link-${v}`}
            onClick={() => onTarget(v)}
            className={`px-3 h-8 text-sm rounded-md border transition-colors ${
              drawTarget === v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </section>
  );
}

export default function SettingsPage() {  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-[1500px] mx-auto">
      <div className="mb-5">
        <h1 className="text-xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>Field</h1>
        <p className="text-sm text-slate-500 mt-1">
          The roster and the field-specific detail behind it — installers, crews and teaming.
          App-wide permissions live under Users.
        </p>
      </div>
      <RoleSection />
      <PreferencesSection />
      <FieldViewsSection />
      <SchedulingSection />
      <GcRulesSection />
      <ScheduleAutomationSection />
      <VehiclesSection />
      <GcConfirmSection />
      <TimekeepingSection />
      <LaborDefaultsSection />
      <Tabs defaultValue="installers" className="w-full">
        <TabsList className="bg-white border border-gray-200 rounded-md h-auto min-h-10 px-1 flex-wrap max-w-full justify-start">
          <TabsTrigger value="installers" data-testid="settings-tab-installers"><User className="w-3.5 h-3.5 mr-1" />Installers</TabsTrigger>
          <TabsTrigger value="crews" data-testid="settings-tab-crews"><Users className="w-3.5 h-3.5 mr-1" />Crews</TabsTrigger>
          <TabsTrigger value="teams" data-testid="settings-tab-teams"><Link2 className="w-3.5 h-3.5 mr-1" />Teams</TabsTrigger>
        </TabsList>
        <div className="mt-4">
          <TabsContent value="installers"><InstallersSection /></TabsContent>
          <TabsContent value="crews"><CrewsSection /></TabsContent>
          <TabsContent value="teams"><TeamsSection /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
